import FormDateTimePicker from '../../../../components/form/FormDateTimePicker';
import FormEmailListField from '../../../../components/form/FormEmailListField';
import FormTextField from '../../../../components/form/FormTextField';
import { __, required } from '../../../../utils';
import type { FormValues, VotingAudience } from './formValues';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Divider,
  Stack,
  SwitchField,
  Typography,
} from '@presov.sk/presov-ui-react';
import { startOfDay } from 'date-fns';
import React from 'react';
import { Field, useForm, useFormState } from 'react-final-form';

type Props = {
  disabled?: boolean;
};

const SectionTitle: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Typography
    variant='overline'
    sx={{ fontSize: 11, fontWeight: 700, color: 'text.secondary' }}
  >
    {children}
  </Typography>
);

const Hint: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Stack
    direction='row'
    alignItems='flex-start'
    gap={1.25}
    sx={({ palette, shape }) => ({
      border: `1px solid ${palette.divider}`,
      borderRadius: `${shape.borderRadius}px`,
      backgroundColor: palette.grey[50],
      px: 1.75,
      py: 1.5,
    })}
  >
    <InfoOutlinedIcon
      sx={{ fontSize: 17, mt: '2px', flex: 'none', color: 'grey.600' }}
    />
    <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: 'grey.600' }}>
      {children}
    </Typography>
  </Stack>
);

/**
 * Výber z troch možností, kde každá nesie **vetu o následku**, nie len meno.
 * Obyčajný `RadioGroup` sem nesedí: rozdiel medzi „prihlásení" a „overené účty"
 * nie je v názve, ale v tom, čo tým úradník získa a čo stratí.
 */
const AudienceOption: React.FC<{
  checked: boolean;
  title: string;
  description: string;
  disabled?: boolean;
  onSelect: () => void;
}> = ({ checked, title, description, disabled, onSelect }) => (
  <Box
    component='label'
    onClick={() => !disabled && onSelect()}
    sx={({ palette, shape }) => ({
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      p: 1.75,
      cursor: disabled ? 'default' : 'pointer',
      borderRadius: `${shape.borderRadius}px`,
      border: `1px solid ${checked ? palette.grey[900] : palette.divider}`,
      backgroundColor: checked ? palette.grey[50] : palette.background.paper,
    })}
  >
    <Box
      aria-hidden
      sx={({ palette, shape }) => ({
        flex: 'none',
        mt: '2px',
        width: 20,
        height: 20,
        display: 'grid',
        placeItems: 'center',
        borderRadius: `${shape.borderRadiusFull}px`,
        border: `2px solid ${checked ? palette.primary.main : palette.grey[400]}`,
      })}
    >
      {checked && (
        <Box
          sx={({ palette, shape }) => ({
            width: 10,
            height: 10,
            borderRadius: `${shape.borderRadiusFull}px`,
            backgroundColor: palette.primary.main,
          })}
        />
      )}
    </Box>
    <Stack gap={0.375}>
      <Typography component='b' sx={{ fontSize: 15, fontWeight: 700 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: 'grey.600' }}>
        {description}
      </Typography>
    </Stack>
  </Box>
);

/**
 * Nastavenia hlasovania - druhá polovica buildera.
 *
 * Sú tri skupiny (zadanie, kedy je otvorené, kto môže hlasovať) a štvrtá
 * o viditeľnosti; každá má vetu o následku. Predtým to bola karta **nad**
 * otázkami, takže úradník musel prejsť cez dátumy a publikum skôr, než sa
 * dostal k tomu, na čo sa vlastne pýta.
 */
const VotingSettingsForm: React.FC<Props> = ({ disabled }) => {
  const { values } = useFormState<FormValues>();
  const form = useForm();

  React.useEffect(() => {
    if (!values.openFrom) {
      form.change('openTo', null);
    }
  }, [form, values.openFrom]);

  const audiences: {
    value: VotingAudience;
    title: string;
    description: string;
  }[] = [
    {
      value: 'anyone',
      title: __('Ktokoľvek, aj neprihlásený'),
      description: __(
        'Anonymný hlas sa nedá spojiť s účtom, takže ten istý človek môže hlasovať znova z iného prehliadača. Vynúti aj verejnú viditeľnosť hlasovania.',
      ),
    },
    {
      value: 'authenticated',
      title: __('Prihlásení používatelia'),
      description: __(
        'Jeden účet, jeden hlas. Odporúčané, keď na výsledku záleží.',
      ),
    },
    {
      value: 'verified',
      title: __('Iba overené účty'),
      description: __(
        'Hlasovať smie len účet s overenou totožnosťou. Zúžite tým počet hlasujúcich.',
      ),
    },
  ];

  return (
    <Stack gap={3.5}>
      <Typography
        component='h2'
        sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.25 }}
      >
        {__('Nastavenia hlasovania')}
      </Typography>

      <Stack gap={1.5}>
        <SectionTitle>{__('Zadanie')}</SectionTitle>
        {/* `mb: 0` z rovnakého dôvodu ako v [[QuestionEditor]]: odstupy medzi
            poľami tu drží `gap` skupiny, nie vlastný spodný okraj poľa - inak
            sa sčítajú a skupiny prestanú byť rovnako ďaleko od seba. */}
        <FormTextField
          sx={{ mb: 0 }}
          name='title'
          label={__('Názov hlasovania')}
          required
          fullWidth
          validate={required}
          disabled={disabled}
        />
        <Stack gap={0.5}>
          <FormTextField
            sx={{ mb: 0 }}
            name='description'
            label={__('Popis')}
            markdown
            required
            fullWidth
            multiline
            rows={4}
            validate={required}
            disabled={disabled}
          />
          <Typography variant='caption' color='text.secondary'>
            {__('Prvé dve vety uvidí občan v zozname hlasovaní.')}
          </Typography>
        </Stack>
      </Stack>

      <Divider />

      <Stack gap={1.5}>
        <SectionTitle>{__('Kedy je otvorené')}</SectionTitle>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
          <Box sx={{ flex: 1 }}>
            <FormDateTimePicker
              name='openFrom'
              label={__('Od')}
              disabled={disabled}
              minDate={new Date()}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <FormDateTimePicker
              name='openTo'
              label={__('Do')}
              disabled={disabled || !values.openFrom}
              minDate={
                values.openFrom
                  ? new Date(values.openFrom)
                  : startOfDay(new Date())
              }
            />
          </Box>
        </Stack>
        <Hint>
          {__(
            'Od otvorenia sa hlasovanie nedá upraviť - otázky, možnosti ani publikum. Bez dátumu ho treba otvoriť aj zavrieť ručne.',
          )}
        </Hint>
      </Stack>

      <Divider />

      <Stack gap={1.5}>
        <SectionTitle>{__('Kto môže hlasovať')}</SectionTitle>
        <Field name='audience'>
          {({ input }) => (
            <Stack gap={1}>
              {audiences.map((audience) => (
                <AudienceOption
                  key={audience.value}
                  checked={input.value === audience.value}
                  title={audience.title}
                  description={audience.description}
                  disabled={disabled}
                  onSelect={() => input.onChange(audience.value)}
                />
              ))}
            </Stack>
          )}
        </Field>
      </Stack>

      <Divider />

      <Stack gap={1.5}>
        <SectionTitle>{__('Viditeľnosť a výsledky')}</SectionTitle>

        <Field name='public' type='checkbox'>
          {({ input }) => (
            <SwitchField
              label={__('Detail vidia aj neprihlásení')}
              description={
                values.audience === 'anyone'
                  ? __(
                      'Pri hlasovaní pre kohokoľvek je verejná viditeľnosť vynútená - inak by sa k nemu neprihlásený nedostal.',
                    )
                  : __('Hlasovanie sa dá otvoriť z odkazu bez prihlásenia.')
              }
              // Pri „ktokoľvek" je verejná viditeľnosť vynútená, takže sa tu
              // nedá vypnúť - vypnutá by hlasovanie skryla pred publikom,
              // ktoré preň bolo zvolené.
              checked={values.audience === 'anyone' ? true : !!input.checked}
              disabled={disabled || values.audience === 'anyone'}
              onChange={(checked) => input.onChange(checked)}
            />
          )}
        </Field>

        {/* Výhybka: „či" stojí nad „kedy". Priebežné výsledky sú vetva
            zverejnenia, nie samostatné rozhodnutie - preto sa pri vypnutom
            prístupe nedajú zapnúť a formulár ich zároveň zhasne, aby v tele
            požiadavky nezostala kombinácia, ktorú zakazuje CHECK v databáze.

            Obmedzuje to **len občana**: správca hlasovaní vidí priebežné aj
            konečné výsledky vždy, o to sa stará `getVotingResults` v API. */}
        <Field name='availableResults' type='checkbox'>
          {({ input }) => (
            <SwitchField
              label={__('Prístup k výsledkom')}
              description={
                input.checked
                  ? __('Zapnuté: výsledky sa zverejnia po uzavretí hlasovania.')
                  : __(
                      'Vypnuté: občan výsledky neuvidí ani po uzavretí, zostane mu len počet hlasov. Vy ich v správe hlasovaní vidíte ďalej.',
                    )
              }
              checked={!!input.checked}
              disabled={disabled}
              onChange={(checked) => {
                input.onChange(checked);
                if (!checked) {
                  form.change('availableIncompleteResults', false);
                }
              }}
            />
          )}
        </Field>

        <Field name='availableIncompleteResults' type='checkbox'>
          {({ input }) => (
            <SwitchField
              label={__('Priebežné výsledky')}
              description={
                !values.availableResults
                  ? __(
                      'Bez prístupu k výsledkom nie je čo zverejňovať priebežne.',
                    )
                  : input.checked
                    ? __(
                        'Zapnuté: podrobné výsledky sú viditeľné počas celého hlasovania.',
                      )
                    : __(
                        'Vypnuté: počas hlasovania je viditeľný len počet hlasov. Podrobnosti sa zverejnia po uzavretí.',
                      )
              }
              checked={values.availableResults ? !!input.checked : false}
              disabled={disabled || !values.availableResults}
              onChange={(checked) => input.onChange(checked)}
            />
          )}
        </Field>
      </Stack>

      <Divider />

      {/* Report je piata skupina a stojí za viditeľnosťou zámerne: hovorí
          o tom, čo dostane **mesto**, nie o tom, čo uvidí občan. Tie dve veci
          sa nemajú miešať - prepínače vyššie výsledky zverejňujú, tento ich
          posiela komisii bez ohľadu na ne. */}
      <Stack gap={1.5}>
        <SectionTitle>{__('Report po uzavretí')}</SectionTitle>

        <Field name='sendReport' type='checkbox'>
          {({ input }) => (
            <SwitchField
              label={__('Odoslať report po ukončení hlasovania')}
              description={
                input.checked
                  ? __(
                      'Po uzavretí odíde na zadané adresy mail s reportom v PDF a dátami v CSV.',
                    )
                  : __(
                      'Výsledky si po uzavretí stiahnete sami v zápise hlasovania.',
                    )
              }
              checked={!!input.checked}
              disabled={disabled}
              onChange={(checked) => {
                input.onChange(checked);
                // Adresy sa nezahadzujú hneď - úradník prepínač často vypne
                // a znova zapne, kým sa rozhoduje. Do tela požiadavky sa
                // nedostanú tak či tak, o to sa stará `toApiValues`.
              }}
            />
          )}
        </Field>

        {values.sendReport && (
          <FormEmailListField
            name='reportEmails'
            label={__('Komu sa report pošle')}
            required
            disabled={disabled}
            info={__(
              'Adresy nemusia patriť používateľom aplikácie - report chodí napríklad komisii alebo externému spracovateľovi.',
            )}
            validate={(emails) =>
              // `required` tu nestačí: prázdne pole je truthy, takže by prešlo.
              // Rovnakú kombináciu odmieta server aj CHECK v databáze.
              Array.isArray(emails) && emails.length > 0
                ? undefined
                : __('Zadajte aspoň jednu adresu.')
            }
          />
        )}

        <Hint>
          {__(
            'Report sa dá zapnúť aj počas hlasovania a stiahnuť kedykoľvek v jeho zápise. Archivované hlasovanie report neodošle - archiváciou sa výsledky rušia.',
          )}
        </Hint>
      </Stack>
    </Stack>
  );
};

export default VotingSettingsForm;
